import type {
  Operator,
  QueueInfo,
  ServerMessage,
  ServiceStatus,
} from '../state/types';

export type StartAnonymousInput = {
  firstName: string;
  lastName: string;
  emailAddress: string;
  initialQuestion: string;
  captchaToken: string;
};

export type StartAuthenticatedInput = {
  /**
   * JWT vers opgehaald van de host-portal (`identityUrl`). Wordt door
   * `HttpChatApiService` als `Authorization: Bearer <jwt>`-header gezet
   * en mag in client-state alleen leven tot SESSION_STARTED (C1) —
   * daarna is de `kccConversationRequestId` het anker.
   */
  jwt: string;
  initialQuestion: string;
};

export type ConversationState = {
  messages: ServerMessage[];
  isEnded: boolean;
  operator: Operator | null;
  agentTyping: boolean;
  queueInfo: QueueInfo | null;
  /**
   * Server-supplied tekst die toelicht waarom de sessie eindigde —
   * bijvoorbeeld "We kunnen helaas geen beschikbare medewerker vinden".
   * Wordt door de reducer als grijze system-pill in de chat-stream gezet.
   * Alleen gevuld bij isEnded=true.
   */
  endMessage?: string;
};

export interface ChatApiService {
  fetchServiceStatus(): Promise<ServiceStatus>;
  startAnonymous(input: StartAnonymousInput): Promise<string>;
  /**
   * Start een conversatie als ingelogde bezoeker. Server bepaalt de
   * `personId` aan de hand van de Bearer-token; client stuurt geen
   * naam/email — die zitten in de identity-claim.
   */
  startAuthenticated(input: StartAuthenticatedInput): Promise<string>;
  getState(sessionId: string): Promise<ConversationState>;
  sendMessage(sessionId: string, text: string): Promise<void>;
  /**
   * Sluit het gesprek aan klant-zijde — operator krijgt direct een
   * "klant verliet"-event i.p.v. via heartbeat-timeout. Best-effort:
   * een falende call blokkeert de UI niet (bezoeker is al onderweg).
   */
  endConversation(sessionId: string): Promise<void>;
  /**
   * Stuurt een rating (1-5) voor een afgesloten gesprek. Best-effort —
   * een falende call mag de UI niet blokkeren omdat de feedback al
   * optimistisch in de UI is bevestigd. Server-side is `/rate` een
   * write-only endpoint zonder body in de response.
   */
  rateConversation(sessionId: string, rating: number): Promise<void>;
}

export class ChatApiError extends Error {
  constructor(
    public readonly kind:
      | 'network'
      | 'captcha_invalid'
      | 'captcha_system_error'
      | 'service_unavailable'
      | 'session_expired'
      | 'unknown',
    message: string,
  ) {
    super(message);
    this.name = 'ChatApiError';
  }
}
